/**
 * NDAGateB — NDA acceptance gate (split layout, locked preview variant).
 *
 * Ported from Yan-Vada Hero Pilot/components/gates.jsx NDAGateB.
 * Two-column: blurred locked pitch preview (left) + NDA panel (right).
 * Dark theme card. Hard gate — cannot view pitch without accepting.
 *
 * Rendered when a protected sponsor API response requires NDA acceptance.
 * Dismissed once accepted; server acceptance is recorded via AuthContext.acceptNDA().
 */

import { useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { GateCheckbox } from './shared'

function sponsorSlugFromPath(pathname: string) {
  const parts = pathname.split('/').filter(Boolean)
  if (parts[0] === 'sponsors' && parts[1]) return parts[1]
  if (parts[0] && !['privacy', 'terms', 'signin', 'request-access', 'account', 'status', 'preview'].includes(parts[0])) return parts[0]
  return 'master'
}

interface NDAGateProps {
  sponsorSlug?: string
}

export default function NDAGate({ sponsorSlug }: NDAGateProps = {}) {
  const { acceptNDA, email, signOut } = useAuth()
  const location = useLocation()
  const [checked, setChecked] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleAccept = async () => {
    if (!checked) return
    setSubmitting(true)
    setError(null)
    try {
      await acceptNDA(sponsorSlug ?? sponsorSlugFromPath(location.pathname))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to record NDA acceptance')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="gate-host" style={{ position: 'fixed', inset: 0, zIndex: 9999 }}>
      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'grid',
          gridTemplateColumns: '1fr 480px',
        }}
      >
        {/* Blurred pitch preview */}
        <div
          className="nda-preview-zone"
          style={{
            position: 'relative',
            background: 'linear-gradient(180deg, var(--earth-bg) 0%, var(--earth-surface) 60%, var(--earth-surface-raised) 100%)',
            overflow: 'hidden',
          }}
        >
          {/* Skeleton shapes mimicking a blurred pitch */}
          <div
            style={{
              position: 'absolute',
              inset: 0,
              filter: 'blur(14px) saturate(0.6)',
              opacity: 0.55,
              padding: 'var(--space-12)',
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--space-6)',
            }}
          >
            <div style={{ height: 14, width: 140, background: 'var(--earth-text-muted)', borderRadius: 3 }} />
            <div style={{ height: 48, width: '70%', background: 'var(--earth-text-primary)', borderRadius: 6 }} />
            <div style={{ height: 20, width: '60%', background: 'var(--earth-text-secondary)', borderRadius: 4 }} />
            <div style={{ height: 20, width: '50%', background: 'var(--earth-text-secondary)', borderRadius: 4 }} />
            <div style={{ marginTop: 40, display: 'flex', gap: 20 }}>
              <div style={{ height: 200, flex: 1.4, background: 'var(--earth-surface-raised)', borderRadius: 8 }} />
              <div style={{ height: 200, flex: 1, background: 'var(--earth-surface-raised)', borderRadius: 8 }} />
            </div>
          </div>

          {/* Lock overlay */}
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: 'rgba(245,241,234,0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <div
              className="nda-lock-icon"
              style={{
                width: 72,
                height: 72,
                borderRadius: '50%',
                background: 'var(--earth-bg)',
                border: '1px solid var(--earth-border)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 12px 40px -10px rgba(0,0,0,0.2)',
              }}
            >
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ color: 'var(--earth-text-primary)' }}>
                <rect x="5" y="11" width="14" height="9" rx="1.5" />
                <path d="M8 11V7a4 4 0 0 1 8 0v4" />
              </svg>
            </div>
          </div>
        </div>

        {/* NDA panel */}
        <div className="nda-panel">
          <span className="legal-eyebrow">NDA v1.0</span>
          <h2 className="nda-heading">Keep it between us.</h2>
          <p className="nda-body">
            You&apos;re about to read a commercial proposal intended for you alone. Don&apos;t forward the link,
            don&apos;t screenshot the pricing for a group chat, don&apos;t publish fragments. Need to share
            it internally? Tell us and we&apos;ll send a copy.
          </p>

          <div className="nda-placeholder-badge">
            Placeholder · OpenCode replaces with real NDA
          </div>

          <GateCheckbox checked={checked} onChange={setChecked} id="nda-b" label="I accept the NDA and will treat this as confidential." />

          <button
            className="btn btn-primary nda-view-btn"
            disabled={!checked || submitting}
            onClick={handleAccept}
            style={{
              marginTop: 'var(--space-6)',
              opacity: checked && !submitting ? 1 : 0.4,
              pointerEvents: checked && !submitting ? 'auto' : 'none',
            }}
          >
            {submitting ? 'Recording acceptance...' : 'View the pitch'}
          </button>
          {error && <p className="nda-body" role="alert" style={{ color: '#C57060' }}>{error}</p>}

          <button
            className="btn btn-ghost nda-signout-btn"
            onClick={signOut}
            style={{ marginTop: 'var(--space-3)' }}
          >
            Sign out
          </button>

          <div className="nda-footer-meta">
            Signed in as <span className="nda-email">{email ?? '—'}</span>
          </div>
        </div>
      </div>
    </div>
  )
}
